import React, { useState, useEffect } from 'react';
import { API } from '../App';

const COMMODITY_LABELS = {
  brent_crude:  { label: 'Brent Crude',    icon: '🛢', category: 'Öl' },
  wti_crude:    { label: 'WTI Crude',       icon: '🛢', category: 'Öl' },
  ttf_gas:      { label: 'TTF Gas',         icon: '🔥', category: 'Gas' },
  lng_asia:     { label: 'LNG Asia JKM',    icon: '🔥', category: 'Gas' },
  coal_api2:    { label: 'Coal API2 (ARA)', icon: '⚫', category: 'Kohle' },
  coal_newc:    { label: 'Coal Newcastle',  icon: '⚫', category: 'Kohle' },
  fuel_oil_380: { label: 'Fuel Oil 380',    icon: '⚙', category: 'Produkte' },
  gasoil:       { label: 'Gasoil 0.1%',     icon: '⛽', category: 'Produkte' },
};

export default function CommodityPricePage() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [history, setHistory]   = useState(null);
  const [histLoading, setHL]    = useState(false);

  useEffect(() => {
    API.get('/commodity-prices')
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const loadHistory = async (key) => {
    setSelected(key); setHL(true);
    try {
      const r = await API.get(`/commodity-prices/${key}/history`);
      setHistory(r.data.history);
    } finally { setHL(false); }
  };

  const prices = data?.prices || {};
  const categories = [...new Set(Object.values(COMMODITY_LABELS).map(c => c.category))];

  const sparkline = (hist) => {
    if (!hist || hist.length < 2) return null;
    const vals  = hist.map(h => h.price);
    const min   = Math.min(...vals);
    const max   = Math.max(...vals);
    const w = 120, h = 40, pad = 4;
    const points = vals.map((v, i) => {
      const x = pad + (i / (vals.length - 1)) * (w - 2 * pad);
      const y = pad + (1 - (v - min) / (max - min || 1)) * (h - 2 * pad);
      return `${x},${y}`;
    }).join(' ');
    const isUp = vals[vals.length - 1] >= vals[0];
    return (
      <svg width={w} height={h} style={{ display:'block' }}>
        <polyline points={points} fill="none" stroke={isUp ? '#22c55e' : '#ef4444'} strokeWidth={1.5} />
      </svg>
    );
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>📈 Commodity Preise</h1>
        <p>Energie-Rohstoffpreise — Reuters / Bloomberg Stub (Demo)</p>
      </div>

      <div style={{ padding:'12px 16px', marginBottom:20, borderRadius:8,
        background:'#f59e0b15', border:'1px solid #f59e0b40', fontSize:13, color:'#f59e0b' }}>
        ℹ Stub-Modus: Verbinde mit Reuters Eikon oder Bloomberg durch Setzen von <code style={{ background:'#f59e0b10', padding:'1px 5px', borderRadius:4 }}>REUTERS_API_KEY</code> oder <code style={{ background:'#f59e0b10', padding:'1px 5px', borderRadius:4 }}>BLOOMBERG_API_KEY</code> in der .env Datei.
      </div>

      {data && (
        <div style={{ fontSize:12, color:'var(--text3)', marginBottom:16 }}>
          Stand: {new Date(data.timestamp).toLocaleString('de-DE')} (Demo-Daten)
        </div>
      )}

      {loading && <div className="loading">Lade Preise…</div>}

      {categories.map(cat => (
        <div key={cat} style={{ marginBottom:24 }}>
          <div style={{ fontSize:12, fontWeight:700, color:'var(--text3)', textTransform:'uppercase',
            letterSpacing:'.1em', marginBottom:10 }}>{cat}</div>
          <div className="grid-2">
            {Object.entries(prices)
              .filter(([k]) => COMMODITY_LABELS[k]?.category === cat)
              .map(([key, p]) => {
                const meta  = COMMODITY_LABELS[key] || { label: key, icon: '📊' };
                const isUp  = p.change >= 0;
                const isSel = selected === key;
                return (
                  <div key={key} className="card" style={{ cursor:'pointer', borderColor: isSel ? 'var(--accent)' : 'var(--border)' }}
                    onClick={() => loadHistory(key)}>
                    <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                      <div style={{ fontSize:28 }}>{meta.icon}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700, fontSize:13, marginBottom:4 }}>{meta.label}</div>
                        <div style={{ display:'flex', alignItems:'baseline', gap:10 }}>
                          <span style={{ fontSize:22, fontWeight:800, color:'var(--text1)' }}>
                            {p.price.toFixed(2)}
                          </span>
                          <span style={{ fontSize:11, color:'var(--text3)' }}>{p.unit}</span>
                        </div>
                        <div style={{ fontSize:12, color: isUp ? '#22c55e' : '#ef4444', marginTop:2 }}>
                          {isUp ? '▲' : '▼'} {Math.abs(p.change).toFixed(2)} ({isUp ? '+' : ''}{p.change_pct?.toFixed(2)}%)
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      ))}

      {selected && (
        <div className="card" style={{ marginTop:8 }}>
          <div className="card-title">
            {COMMODITY_LABELS[selected]?.icon} {COMMODITY_LABELS[selected]?.label} — 30-Tage Verlauf (Demo)
          </div>
          {histLoading && <div className="loading">Lade Verlauf…</div>}
          {history && !histLoading && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12 }}>
                <span style={{ fontSize:12, color:'var(--text3)' }}>
                  Min: {Math.min(...history.map(h=>h.price)).toFixed(2)} — Max: {Math.max(...history.map(h=>h.price)).toFixed(2)}
                </span>
                <span style={{ fontSize:12, color:'var(--text3)' }}>
                  {history[0]?.date} → {history[history.length-1]?.date}
                </span>
              </div>

              {/* Simple bar chart */}
              {(() => {
                const vals = history.map(h => h.price);
                const min  = Math.min(...vals);
                const max  = Math.max(...vals);
                const last = vals[vals.length - 1];
                return (
                  <div style={{ display:'flex', alignItems:'flex-end', gap:2, height:120 }}>
                    {history.map((h, i) => {
                      const pct = (h.price - min) / (max - min || 1);
                      const isLast = i === history.length - 1;
                      return (
                        <div key={i} title={`${h.date}: ${h.price.toFixed(2)}`}
                          style={{ flex:1, height:`${20 + pct * 80}%`, borderRadius:'2px 2px 0 0',
                            background: isLast ? 'var(--accent)' : h.price > last ? '#3b82f660' : '#ef444460',
                            minWidth:2 }} />
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
